// PDVThemes.ts
import { useMemo } from 'react';

export type ThemeVariant = 'verde' | 'verdeclaro' | 'verdeescuro' | 'azul' | 'azulclaro' | 'azulescuro' | 'basico' | 'ocean' | 'petroleo' | 'azulpetroleo' | 'sunset' | 'goldenriver' | 'roxo' | 'rosa' | 'grafite' | 'mercado' | 'laranja' | 'amarelo' | 'amareloclaro' | 'amareloescuro' | 'siac';

export interface PDVThemeColors {
  // Cores principais
  primaryGradient: string;
  primarySolid: string;
  primaryDark: string;

  // Cores de superfície
  surface: string;
  surfaceHover: string;
  glass: string;

  // Cores de borda
  border: string;

  // Cores de texto
  textPrimary: string;
  textSecondary: string;
  textOnPrimary: string;

  // Estados
  hoverGradient: string;

  // Fundo da tela
  backgroundImage?: string;
  backgroundColor?: string;
  overlay?: string;

  // Componentes específicos
  cartBackground: string;
  cartItemBackground: string;
  cartItemHover: string;
  inputBackground: string;

  // Barra de lançamento (Quantidade | Descrição | Valor Unitário) — ver
  // BarraLancamento em Pdv.tsx. Opcionais: quando ausentes, a barra cai de
  // volta em primaryDark/primarySolid/textOnPrimary (bom o bastante pros
  // temas mais antigos, que nunca foram pensados pra essa barra).
  barDark?: string;
  barAccent?: string;
  barDarkText?: string;
  barAccentText?: string;

  // Cor de nomes/ícones do rodapé (Caixa/Operador, atalhos) quando o fundo
  // do PDV é escuro — ver PDVFooter/PDVShortcut. Opcional: quando ausente,
  // cai de volta em primarySolid (o que já funcionava bem pros temas de
  // foto mais antigos, cuja cor de destaque já é clara o bastante). Alguns
  // temas escuros mais recentes (ex.: grafite) têm primarySolid num tom
  // mais fechado — usado em vários outros lugares (ex.: fundo do segmento
  // "Valor" da barra de lançamento) — que não pode simplesmente ser
  // clareado sem quebrar esses outros usos; footerAccent existe pra
  // resolver só o rodapé, sem mexer no resto do tema.
  footerAccent?: string;
}

export const getThemeColors = (variant: ThemeVariant): PDVThemeColors => {
  switch (variant) {
    case 'verde':
      return {
        // 🟢 TEMA VERDE MODERNO
        primaryGradient: `
          linear-gradient(135deg,
            #059669 0%,
            #047857 25%,
            #065F46 50%,
            #064E3B 100%
          )
        `,
        primarySolid: '#059669',
        primaryDark: '#065F46',

        surface: 'rgba(240, 253, 244, 0.95)',
        surfaceHover: 'rgba(220, 252, 231, 1)',
        glass: 'rgba(5, 150, 105, 0.35)',

        border: 'rgba(5, 150, 105, 0.25)',

        textPrimary: '#064E3B',
        textSecondary: '#065F46',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(5, 150, 105, 0.85) 0%,
            rgba(4, 120, 87, 0.85) 25%,
            rgba(6, 95, 70, 0.85) 50%,
            rgba(6, 78, 59, 0.85) 100%
          )
        `,

        // Sem foto de fundo (a pedido) — fundo liso verde bem claro, como
        // os temas roxo/rosa.
        backgroundImage: undefined,
        backgroundColor: '#ECFDF5',
        overlay: 'linear-gradient(rgba(6, 78, 59, 0.05), rgba(6, 78, 59, 0.05))',

        cartBackground: 'rgba(240, 253, 244, 0.95)',
        cartItemBackground: 'rgba(220, 252, 231, 0.85)',
        cartItemHover: 'rgba(187, 247, 208, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #059669 0%,
            #047857 100%
        )`,
      };
    case 'verdeclaro':
      return {
        // 🟢 TEMA VERDE CLARO — mesmo esquema do 'verde', um tom mais vivo
        primaryGradient: `
          linear-gradient(135deg,
            #4ADE80 0%,
            #22C55E 35%,
            #16A34A 70%,
            #15803D 100%
          )
        `,
        primarySolid: '#22C55E',
        primaryDark: '#15803D',

        surface: 'rgba(247, 254, 249, 0.95)',
        surfaceHover: 'rgba(220, 252, 231, 1)',
        glass: 'rgba(34, 197, 94, 0.3)',

        border: 'rgba(34, 197, 94, 0.25)',

        textPrimary: '#14532D',
        textSecondary: '#166534',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(74, 222, 128, 0.85) 0%,
            rgba(34, 197, 94, 0.85) 35%,
            rgba(22, 163, 74, 0.85) 70%,
            rgba(21, 128, 61, 0.85) 100%
          )
        `,

        backgroundImage: undefined,
        backgroundColor: '#F0FDF4',
        overlay: 'linear-gradient(rgba(21, 128, 61, 0.04), rgba(21, 128, 61, 0.04))',

        cartBackground: 'rgba(247, 254, 249, 0.95)',
        cartItemBackground: 'rgba(220, 252, 231, 0.85)',
        cartItemHover: 'rgba(187, 247, 208, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #22C55E 0%,
            #16A34A 100%
        )`,
      };

    case 'verdeescuro':
      return {
        // 🌲 TEMA VERDE ESCURO — verde floresta, fundo claro com destaque
        // fechado (o 'verde' fica no meio, o 'verdeclaro' no tom mais vivo)
        primaryGradient: `
          linear-gradient(135deg,
            #166534 0%,
            #14532D 40%,
            #0F3D22 75%,
            #052E16 100%
          )
        `,
        primarySolid: '#166534',
        primaryDark: '#052E16',

        surface: 'rgba(240, 247, 242, 0.95)',
        surfaceHover: 'rgba(220, 237, 225, 1)',
        glass: 'rgba(22, 101, 52, 0.35)',

        border: 'rgba(22, 101, 52, 0.25)',

        textPrimary: '#052E16',
        textSecondary: '#14532D',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(22, 101, 52, 0.85) 0%,
            rgba(20, 83, 45, 0.85) 40%,
            rgba(15, 61, 34, 0.85) 75%,
            rgba(5, 46, 22, 0.85) 100%
          )
        `,

        backgroundImage: undefined,
        backgroundColor: '#E8F2EB',
        overlay: 'linear-gradient(rgba(5, 46, 22, 0.06), rgba(5, 46, 22, 0.06))',

        cartBackground: 'rgba(240, 247, 242, 0.95)',
        cartItemBackground: 'rgba(220, 237, 225, 0.85)',
        cartItemHover: 'rgba(200, 225, 208, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #166534 0%,
            #14532D 100%
        )`,
      };

    case 'sunset':
      return {
        // 🌅 TEMA SUNSET TROPICAL – inspirado no pôr do sol
        primaryGradient: `
      linear-gradient(135deg,
        #FDBA74 0%,   /* dourado suave */
        #FB923C 35%,  /* laranja pôr do sol */
        #C2410C 70%,  /* laranja queimado */
        #7C2D12 100%  /* marrom profundo */
      )
    `,
        primarySolid: '#FB923C',   // laranja elegante
        primaryDark: '#7C2D12',    // marrom escuro

        /* Superfícies claras para contraste */
        surface: 'rgba(255, 255, 255, 0.95)',
        surfaceHover: 'rgba(255, 255, 255, 0.98)',
        glass: 'rgba(251, 146, 60, 0.22)',

        /* Bordas quentes e discretas */
        border: 'rgba(124, 45, 18, 0.25)',

        /* Textos neutros (não usar laranja no texto!) */
        textPrimary: '#1F2937',   // grafite
        textSecondary: '#4B5563', // cinza médio
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
      linear-gradient(135deg,
        rgba(253, 186, 116, 0.9) 0%,
        rgba(251, 146, 60, 0.9) 35%,
        rgba(194, 65, 12, 0.9) 70%,
        rgba(124, 45, 18, 0.9) 100%
      )
    `,

        /* Fundo da praia ao pôr do sol */
        backgroundImage: 'url(/img/amarela.jpg)',
        backgroundColor: '#FFF7ED',

        /* Overlay quente + leve escurecimento inferior */
        overlay: `
      linear-gradient(
        180deg,
        rgba(255, 200, 140, 0.18) 0%,
        rgba(255, 180, 100, 0.08) 40%,
        rgba(0, 0, 0, 0.35) 100%
      )
    `,

        /* Carrinho */
        cartBackground: 'rgba(255, 255, 255, 0.96)',
        cartItemBackground: 'rgba(255, 247, 237, 0.9)',
        cartItemHover: 'rgba(254, 215, 170, 0.6)',

        /* Inputs quentes e elegantes */
        inputBackground: `
      linear-gradient(135deg,
        #FB923C 0%,
        #C2410C 100%
      )
    `,
      };
    case 'ocean':
      return {
        primaryGradient: `
      linear-gradient(135deg,
        #6AD4DE 0%,
        #40CEDB 50%,
        #2DB8C5 100%
      )
    `,

        primarySolid: '#6AD4DE',
        primaryDark: '#40CEDB',

        /* Fundo principal — "oceano profundo" escuro, 100% CSS (sem
           foto/arquivo de imagem, mesma filosofia da LoginTechDecoration).
           Dois brilhos radiais em ciano (reflexo de luz na água) por cima de
           um gradiente diagonal escuro azul-petróleo→teal. As camadas de
           corte diagonal nos cantos e grade de pontos (que dependem de
           mask-image, e por isso não cabem num background-image só) ficam
           em PDVOceanDecoration.tsx, renderizado por cima deste fundo em
           Pdv.tsx quando o tema é 'ocean'. */
        backgroundImage: `
      radial-gradient(circle at 78% 18%, rgba(42, 199, 210, 0.16) 0%, rgba(42, 199, 210, 0.05) 25%, transparent 48%),
      radial-gradient(circle at 12% 82%, rgba(25, 154, 172, 0.12) 0%, transparent 42%),
      linear-gradient(135deg, #062c3a 0%, #073747 35%, #084252 68%, #075260 100%)
    `,
        backgroundColor: '#062c3a',

        /* Cards */
        surface: 'rgba(255,255,255,0.95)',
        surfaceHover: 'rgba(255,255,255,1)',

        /* Glass */
        glass: 'rgba(106,212,222,0.18)',

        /* Bordas */
        border: 'rgba(64,206,219,0.20)',

        /* Textos */
        textPrimary: '#1F2937',
        textSecondary: '#4B5563',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
      linear-gradient(135deg,
        rgba(106,212,222,0.95) 0%,
        rgba(64,206,219,0.95) 100%
      )
    `,

        overlay: `
      linear-gradient(
        180deg,
        rgba(0,0,0,0.06) 0%,
        rgba(0,0,0,0.1) 100%
      )
    `,

        cartBackground: 'rgba(255,255,255,0.96)',
        cartItemBackground: 'rgba(248,250,252,0.95)',
        cartItemHover: 'rgba(106,212,222,0.12)',

        inputBackground: `
      linear-gradient(135deg,
        #6AD4DE 0%,
        #40CEDB 100%
      )
    `,
      };

    case 'goldenriver':
      return {
        // 🌅 TEMA GOLDEN RIVER – inspirado no sol, rio e névoa
        primaryGradient: `
      linear-gradient(135deg,
        #EAB308 0%,   /* dourado sol (mais fechado, menos brilho) */
        #F59E0B 35%,  /* âmbar */
        #D97706 65%,  /* laranja queimado */
        #92400E 100%  /* marrom profundo */
      )
    `,
        primarySolid: '#F59E0B',
        primaryDark: '#92400E',

        /* 🧊 Superfícies glass */
        surface: 'rgba(255, 255, 255, 0.88)',
        surfaceHover: 'rgba(255, 255, 255, 0.94)',

        /* GLASS real (header, cards flutuantes) */
        glass: 'rgba(255, 200, 120, 0.22)',

        /* Bordas suaves e quentes */
        border: 'rgba(146, 64, 14, 0.22)',

        /* Textos elegantes */
        textPrimary: '#1F2937',      // grafite
        textSecondary: '#4B5563',    // cinza quente
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
      linear-gradient(135deg,
        rgba(234, 179, 8, 0.9) 0%,
        rgba(245, 158, 11, 0.9) 35%,
        rgba(217, 119, 6, 0.9) 65%,
        rgba(146, 64, 14, 0.9) 100%
      )
    `,

        /* Fundo da tela */
        backgroundImage: 'url(/img/floresta.jpg)', // essa imagem
        backgroundColor: '#1C1917',

        /* Overlay dourado + filtro bem forte no topo (sol estourado na foto) */
        overlay: `
      linear-gradient(
        180deg,
        rgba(15, 9, 3, 0.8) 0%,
        rgba(15, 9, 3, 0.55) 14%,
        rgba(15, 9, 3, 0.22) 26%,
        rgba(0, 0, 0, 0.08) 42%,
        rgba(0, 0, 0, 0.4) 100%
      )
    `,

        /* Carrinho */
        cartBackground: 'rgba(255, 255, 255, 0.92)',
        cartItemBackground: 'rgba(255, 247, 237, 0.9)',
        cartItemHover: 'rgba(254, 215, 170, 0.65)',


        inputBackground: `
      linear-gradient(135deg,
        #F59E0B 0%,
        #D97706 100%
      )
    `,
      };

    case 'azul':
      return {
        // 🔵 TEMA AZUL ELEGANTE
        primaryGradient: `
          linear-gradient(135deg,
            #0EA5E9 0%,
            #0284C7 25%,
            #0369A1 50%,
            #075985 100%
        )`,
        primarySolid: '#0EA5E9',
        primaryDark: '#0369A1',

        surface: 'rgba(240, 249, 255, 0.95)',
        surfaceHover: 'rgba(224, 242, 254, 1)',
        glass: 'rgba(14, 165, 233, 0.35)',

        border: 'rgba(14, 165, 233, 0.25)',

        textPrimary: '#0C4A6E',
        textSecondary: '#075985',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(14, 165, 233, 0.85) 0%,
            rgba(2, 132, 199, 0.85) 25%,
            rgba(3, 105, 161, 0.85) 50%,
            rgba(7, 89, 133, 0.85) 100%
        )`,

        // Fundo principal — azul claro elegante, 100% CSS (sem foto/arquivo
        // de imagem, mesma filosofia da LoginTechDecoration). Três brilhos
        // radiais azulados por cima de um gradiente diagonal claro. As
        // formas geométricas nos cantos, a grade de pontos (dependem de
        // mask-image) e as duas formas rotacionadas (dependem de
        // box-shadow) não cabem num background-image só — ficam em
        // PDVAzulDecoration.tsx, renderizado por cima deste fundo em
        // Pdv.tsx quando o tema é 'azul'.
        backgroundImage: `
      radial-gradient(ellipse at 92% 22%, rgba(0, 153, 219, 0.15) 0%, rgba(0, 153, 219, 0.06) 25%, transparent 48%),
      radial-gradient(ellipse at 8% 82%, rgba(18, 169, 224, 0.13) 0%, rgba(18, 169, 224, 0.04) 27%, transparent 48%),
      radial-gradient(ellipse at 58% 100%, rgba(0, 199, 220, 0.07) 0%, transparent 35%),
      linear-gradient(135deg, #f7fbfe 0%, #eef7fc 38%, #e5f2fa 70%, #dcecf7 100%)
    `,
        backgroundColor: '#F0F9FF',
        overlay: 'linear-gradient(rgba(12, 74, 110, 0.08), rgba(12, 74, 110, 0.08))',

        cartBackground: 'rgba(240, 249, 255, 0.95)',
        cartItemBackground: 'rgba(224, 242, 254, 0.85)',
        cartItemHover: 'rgba(186, 230, 253, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #0EA5E9 0%,
            #0284C7 100%
        )`,
      };
    case 'azulclaro':
      return {
        // 🔵 TEMA AZUL CLARO — mesmo esquema do 'azul', um tom mais claro/vivo
        // (igual 'verdeclaro' é pro 'verde'). 'azul' continua sendo o tom do
        // meio da família, sem nenhuma mudança.
        primaryGradient: `
          linear-gradient(135deg,
            #7DD3FC 0%,
            #38BDF8 35%,
            #0EA5E9 70%,
            #0284C7 100%
        )`,
        primarySolid: '#38BDF8',
        primaryDark: '#0284C7',

        surface: 'rgba(240, 253, 255, 0.95)',
        surfaceHover: 'rgba(224, 249, 255, 1)',
        glass: 'rgba(56, 189, 248, 0.3)',

        border: 'rgba(56, 189, 248, 0.22)',

        textPrimary: '#0C4A6E',
        textSecondary: '#075985',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(125, 211, 252, 0.85) 0%,
            rgba(56, 189, 248, 0.85) 35%,
            rgba(14, 165, 233, 0.85) 70%,
            rgba(2, 132, 199, 0.85) 100%
        )`,

        // Fundo com moldura suave (brilho radial bem sutil nas bordas
        // superior/inferior) em vez de um branco liso — mesma ideia do tema
        // 'azul'/'basico', só mais discreta (sem decoração extra de canto).
        backgroundImage: `
          radial-gradient(ellipse at 50% 0%, rgba(56, 189, 248, 0.10) 0%, transparent 45%),
          radial-gradient(ellipse at 50% 100%, rgba(14, 165, 233, 0.08) 0%, transparent 45%),
          linear-gradient(135deg, #fbfeff 0%, #f0fbff 50%, #e6f7fe 100%)
        `,
        backgroundColor: '#F0FBFF',
        overlay: 'linear-gradient(rgba(12, 74, 110, 0.04), rgba(12, 74, 110, 0.04))',

        cartBackground: 'rgba(240, 253, 255, 0.95)',
        cartItemBackground: 'rgba(224, 249, 255, 0.85)',
        cartItemHover: 'rgba(186, 240, 253, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #38BDF8 0%,
            #0EA5E9 100%
        )`,
      };

    case 'azulescuro':
      return {
        // 🔵 TEMA AZUL ESCURO — navy profundo, mesma família do 'azul'/
        // 'azulclaro', tom mais fechado (igual 'verdeescuro' é pro 'verde').
        primaryGradient: `
          linear-gradient(135deg,
            #3B82F6 0%,
            #2563EB 35%,
            #1D4ED8 70%,
            #1E3A8A 100%
        )`,
        primarySolid: '#2563EB',
        primaryDark: '#1E3A8A',

        surface: 'rgba(239, 246, 255, 0.95)',
        surfaceHover: 'rgba(219, 234, 254, 1)',
        glass: 'rgba(37, 99, 235, 0.3)',

        border: 'rgba(37, 99, 235, 0.22)',

        textPrimary: '#1E3A8A',
        textSecondary: '#1D4ED8',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(59, 130, 246, 0.85) 0%,
            rgba(37, 99, 235, 0.85) 35%,
            rgba(29, 78, 216, 0.85) 70%,
            rgba(30, 58, 138, 0.85) 100%
        )`,

        // Mesma moldura suave do 'azulclaro', tom mais fechado (navy) nos
        // brilhos de borda.
        backgroundImage: `
          radial-gradient(ellipse at 50% 0%, rgba(37, 99, 235, 0.09) 0%, transparent 45%),
          radial-gradient(ellipse at 50% 100%, rgba(30, 58, 138, 0.07) 0%, transparent 45%),
          linear-gradient(135deg, #f8faff 0%, #eef2fc 50%, #e3e9f7 100%)
        `,
        backgroundColor: '#EEF2FC',
        overlay: 'linear-gradient(rgba(30, 58, 138, 0.05), rgba(30, 58, 138, 0.05))',

        cartBackground: 'rgba(239, 246, 255, 0.95)',
        cartItemBackground: 'rgba(219, 234, 254, 0.85)',
        cartItemHover: 'rgba(191, 219, 254, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #2563EB 0%,
            #1E3A8A 100%
        )`,
      };

    case 'petroleo':
      return {
        // 🌊 TEMA ARDÓSIA (petróleo) — header com mosaico 100% CSS (glow
        // radial + diagonal, ver PDVPetroleoHeaderDecoration em Pdv.tsx).
        primaryGradient: `
      radial-gradient(circle at 28% 50%, rgba(0, 197, 215, 0.10) 0%, transparent 24%),
      linear-gradient(110deg, #07536a 0%, #08637a 32%, #06495f 68%, #04394e 100%)
    `,
        primarySolid: '#5F9EA0',
        primaryDark: '#2F4F4F',

        /* Superfícies claras para contraste com o fundo */
        surface: 'rgba(255, 255, 255, 0.94)',
        surfaceHover: 'rgba(255, 255, 255, 0.98)',
        glass: 'rgba(95, 158, 160, 0.25)',

        /* Bordas sutis, nada chamativo */
        border: 'rgba(47, 79, 79, 0.18)',

        /* Textos naturais (sem azul forte) */
        textPrimary: '#1F2937',     // quase grafite
        textSecondary: '#475569',   // cinza azulado
        textOnPrimary: '#FFFFFF',

        // Ícones/nomes do rodapé (Caixa/Operador, atalhos) em branco — a
        // pedido, em vez de primarySolid (o teal do tema), que sobre a foto
        // de fundo ficava discreto demais.
        footerAccent: '#FFFFFF',

        hoverGradient: `
      linear-gradient(135deg,
        rgba(95, 158, 160, 0.85) 0%,
        rgba(74, 124, 140, 0.85) 40%,
        rgba(58, 95, 111, 0.85) 70%,
        rgba(47, 79, 79, 0.85) 100%
      )
    `,

        /* Fundo azul petróleo */
        backgroundImage: 'url(/img/fundo3.jpg)',
        backgroundColor: '#E5EEF0',

        /* Overlay inspirado na luz dourada do sol */
        overlay: `
      linear-gradient(
        180deg,
        rgba(255, 210, 140, 0.12) 0%,
        rgba(255, 255, 255, 0.04) 35%,
        rgba(0, 0, 0, 0.15) 100%
      )
    `,

        /* Carrinho */
        cartBackground: 'rgba(255, 255, 255, 0.95)',
        cartItemBackground: 'rgba(248, 250, 252, 0.9)',
        cartItemHover: 'rgba(226, 232, 240, 1)',

        /* Inputs com azul natural */
        inputBackground: `
      linear-gradient(135deg,
        #5F9EA0 0%,
        #4A7C8C 100%
      )
    `,
      };

    case 'azulpetroleo':
      return {
        // 🌊 TEMA AZUL PETRÓLEO — guia de aplicação próprio (paleta escura
        // fechada, sem foto/degradê decorativo — só as cores/gradientes do
        // guia): #0B2F3D fundo primário, #123F51 superfície elevada
        // (barras/cards/tabelas/modais), #1E5668 superfície secundária
        // (campos/inputs/áreas interativas), #00A5B5 destaques/ações
        // importantes (não usar #36C0CC como cor principal de botão — esse
        // é só pra hover/estados ativos), #E6F7F9/#9FB6BF texto
        // principal/secundário.
        primaryGradient: `linear-gradient(135deg, #123F51 0%, #0B2F3D 100%)`,
        primarySolid: '#00A5B5',
        primaryDark: '#0B2F3D',

        surface: 'rgba(18, 63, 81, 0.95)',
        surfaceHover: 'rgba(54, 192, 204, 0.18)',
        glass: 'rgba(0, 165, 181, 0.30)',

        border: 'rgba(159, 182, 191, 0.25)',

        textPrimary: '#E6F7F9',
        textSecondary: '#9FB6BF',
        textOnPrimary: '#FFFFFF',

        // Ícones/nomes do rodapé — cor de destaque do guia ("ícones
        // ativos: #00A5B5"), não a genérica primarySolid->texto claro dos
        // outros temas escuros (aqui já É a mesma cor, só deixando
        // explícito e fácil de re-tocar sem mexer em primarySolid).
        footerAccent: '#00A5B5',

        hoverGradient: `linear-gradient(135deg, rgba(0, 165, 181, 0.85) 0%, rgba(54, 192, 204, 0.85) 100%)`,

        // Fundo da aplicação — gradiente de superfície do guia (não é foto
        // nem decoração com formas; só o degradê definido lá).
        backgroundImage: `linear-gradient(135deg, #0E353F 0%, #0B2F3D 100%)`,
        backgroundColor: '#0B2F3D',
        overlay: 'linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))',

        cartBackground: 'rgba(18, 63, 81, 0.95)',
        cartItemBackground: 'rgba(30, 86, 104, 0.85)',
        cartItemHover: 'rgba(54, 192, 204, 0.20)',
        inputBackground: `linear-gradient(135deg, #1E5668 0%, #123F51 100%)`,

        // Barra de lançamento (Quantidade | Descrição | Valor Unitário) —
        // sem isso, cairia em primaryDark (quase idêntico ao fundo da
        // página, #0B2F3D) e a barra sumiria contra o fundo.
        barDark: '#123F51',
        barAccent: '#00A5B5',
        barDarkText: '#E6F7F9',
        barAccentText: '#FFFFFF',
      };

    case 'roxo':
      return {
        // 🟣 TEMA ROXO MODERNO
        primaryGradient: `
          linear-gradient(135deg,
            #8B5CF6 0%,
            #7C3AED 35%,
            #6D28D9 70%,
            #4C1D95 100%
        )`,
        primarySolid: '#7C3AED',
        primaryDark: '#4C1D95',

        surface: 'rgba(250, 245, 255, 0.95)',
        surfaceHover: 'rgba(243, 232, 255, 1)',
        glass: 'rgba(124, 58, 237, 0.3)',

        border: 'rgba(124, 58, 237, 0.22)',

        textPrimary: '#1F2937',
        textSecondary: '#4B5563',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(139, 92, 246, 0.85) 0%,
            rgba(124, 58, 237, 0.85) 35%,
            rgba(109, 40, 217, 0.85) 70%,
            rgba(76, 29, 149, 0.85) 100%
        )`,

        backgroundImage: undefined,
        backgroundColor: '#FAF5FF',
        overlay: 'linear-gradient(rgba(76, 29, 149, 0.05), rgba(76, 29, 149, 0.05))',

        cartBackground: 'rgba(250, 245, 255, 0.95)',
        cartItemBackground: 'rgba(243, 232, 255, 0.85)',
        cartItemHover: 'rgba(233, 213, 255, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #8B5CF6 0%,
            #6D28D9 100%
        )`,
      };

    case 'rosa':
      return {
        // 🌸 TEMA ROSA VIBRANTE
        primaryGradient: `
          linear-gradient(135deg,
            #F472B6 0%,
            #EC4899 35%,
            #DB2777 70%,
            #9D174D 100%
        )`,
        primarySolid: '#EC4899',
        primaryDark: '#9D174D',

        surface: 'rgba(255, 245, 250, 0.95)',
        surfaceHover: 'rgba(252, 231, 243, 1)',
        glass: 'rgba(236, 72, 153, 0.3)',

        border: 'rgba(236, 72, 153, 0.22)',

        textPrimary: '#1F2937',
        textSecondary: '#4B5563',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(244, 114, 182, 0.85) 0%,
            rgba(236, 72, 153, 0.85) 35%,
            rgba(219, 39, 119, 0.85) 70%,
            rgba(157, 23, 77, 0.85) 100%
        )`,

        backgroundImage: undefined,
        backgroundColor: '#FDF2F8',
        overlay: 'linear-gradient(rgba(157, 23, 77, 0.05), rgba(157, 23, 77, 0.05))',

        cartBackground: 'rgba(255, 245, 250, 0.95)',
        cartItemBackground: 'rgba(252, 231, 243, 0.85)',
        cartItemHover: 'rgba(251, 207, 232, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #F472B6 0%,
            #DB2777 100%
        )`,
      };

    case 'grafite':
      return {
        // ⚫ TEMA GRAFITE (MODO ESCURO)
        primaryGradient: `
          linear-gradient(135deg,
            #475569 0%,
            #334155 40%,
            #1E293B 75%,
            #0F172A 100%
        )`,
        primarySolid: '#475569',
        primaryDark: '#0F172A',

        surface: 'rgba(30, 41, 59, 0.95)',
        surfaceHover: 'rgba(51, 65, 85, 1)',
        glass: 'rgba(71, 85, 105, 0.35)',

        border: 'rgba(148, 163, 184, 0.25)',

        textPrimary: '#F1F5F9',
        textSecondary: '#CBD5E1',
        textOnPrimary: '#FFFFFF',

        // Ícones/nomes do rodapé (Caixa/Operador, atalhos) — cor mais clara
        // do tema (mesmo tom de textPrimary, quase branco) em vez de
        // primarySolid (usado também como fundo do segmento "Valor" da
        // barra de lançamento, então não pode virar claro sem gerar texto
        // branco sobre fundo branco lá). Bate com a cor da logo do header,
        // que já fica branca (invertida) sobre esse mesmo fundo escuro.
        footerAccent: '#F1F5F9',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(71, 85, 105, 0.85) 0%,
            rgba(51, 65, 85, 0.85) 40%,
            rgba(30, 41, 59, 0.85) 75%,
            rgba(15, 23, 42, 0.85) 100%
        )`,

        // Fundo principal — azul-marinho escuro, 100% CSS (sem foto/arquivo
        // de imagem, mesma filosofia da LoginTechDecoration). Dois brilhos
        // radiais azulados (canto superior-direito e inferior-esquerdo) por
        // cima de um gradiente diagonal escuro quase-preto→azul-marinho. As
        // linhas diagonais nos cantos, a grade de pontos (dependem de
        // mask-image) e as duas formas rotacionadas (dependem de
        // box-shadow) não cabem num background-image só — ficam em
        // PDVGrafiteDecoration.tsx, renderizado por cima deste fundo em
        // Pdv.tsx quando o tema é 'grafite'.
        backgroundImage: `
      radial-gradient(ellipse at 82% 18%, rgba(65, 104, 165, 0.15) 0%, rgba(39, 70, 120, 0.07) 25%, transparent 48%),
      radial-gradient(ellipse at 10% 80%, rgba(51, 83, 135, 0.12) 0%, transparent 42%),
      linear-gradient(135deg, #111d31 0%, #101c30 35%, #0c172a 68%, #091426 100%)
    `,
        backgroundColor: '#091426',
        overlay: 'linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))',

        cartBackground: 'rgba(30, 41, 59, 0.95)',
        cartItemBackground: 'rgba(51, 65, 85, 0.85)',
        cartItemHover: 'rgba(71, 85, 105, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #475569 0%,
            #1E293B 100%
        )`,
      };

    case 'mercado':
      return {
        // 🧺 TEMA MERCADO – prateleiras e produtos, clima de mercearia
        primaryGradient: `
          linear-gradient(135deg,
            #EF4444 0%,
            #DC2626 35%,
            #B91C1C 70%,
            #7F1D1D 100%
          )
        `,
        primarySolid: '#DC2626',
        primaryDark: '#7F1D1D',

        surface: 'rgba(255, 255, 255, 0.95)',
        surfaceHover: 'rgba(255, 255, 255, 0.98)',
        glass: 'rgba(220, 38, 38, 0.22)',

        border: 'rgba(127, 29, 29, 0.2)',

        textPrimary: '#1F2937',
        textSecondary: '#4B5563',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(239, 68, 68, 0.9) 0%,
            rgba(220, 38, 38, 0.9) 35%,
            rgba(185, 28, 28, 0.9) 70%,
            rgba(127, 29, 29, 0.9) 100%
          )
        `,

        backgroundImage: undefined,
        backgroundColor: '#F3E4D0',
        overlay: 'linear-gradient(rgba(127, 29, 29, 0.05), rgba(127, 29, 29, 0.05))',

        cartBackground: 'rgba(255, 255, 255, 0.96)',
        cartItemBackground: 'rgba(254, 242, 242, 0.9)',
        cartItemHover: 'rgba(254, 202, 202, 0.7)',

        inputBackground: `
          linear-gradient(135deg,
            #EF4444 0%,
            #B91C1C 100%
          )
        `,
      };

    case 'amareloclaro':
      return {
        // 🟡 TEMA AMARELO CLARO — amarelo vivo, tom mais claro da família
        // amarelo/laranja (amareloclaro → amarelo → amareloescuro, mesmo
        // esquema de 3 tons do verde/azul). Diferente do tema 'laranja'
        // (layout próprio, cabeçalho escuro) — esses três são lisos/claros,
        // mesmo estilo do 'verde'/'roxo'.
        primaryGradient: `
          linear-gradient(135deg,
            #FDE047 0%,
            #FACC15 35%,
            #EAB308 70%,
            #CA8A04 100%
        )`,
        primarySolid: '#EAB308',
        primaryDark: '#A16207',

        surface: 'rgba(255, 253, 235, 0.95)',
        surfaceHover: 'rgba(254, 249, 195, 1)',
        glass: 'rgba(234, 179, 8, 0.3)',

        border: 'rgba(202, 138, 4, 0.25)',

        textPrimary: '#713F12',
        textSecondary: '#854D0E',
        // Amarelo é claro demais pra texto branco em cima — usa um marrom
        // bem escuro (mesmo critério do 'siac' com barAccentText no ciano
        // claro) pra manter contraste em cima do primaryGradient/botões.
        textOnPrimary: '#422006',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(253, 224, 71, 0.9) 0%,
            rgba(250, 204, 21, 0.9) 35%,
            rgba(234, 179, 8, 0.9) 70%,
            rgba(202, 138, 4, 0.9) 100%
        )`,

        // Moldura suave (brilho radial sutil nas bordas) em vez de branco
        // liso — mesma ideia do 'azulclaro'.
        backgroundImage: `
          radial-gradient(ellipse at 50% 0%, rgba(250, 204, 21, 0.12) 0%, transparent 45%),
          radial-gradient(ellipse at 50% 100%, rgba(234, 179, 8, 0.09) 0%, transparent 45%),
          linear-gradient(135deg, #fffef5 0%, #fffbe8 50%, #fef9dc 100%)
        `,
        backgroundColor: '#FFFBEB',
        overlay: 'linear-gradient(rgba(113, 63, 18, 0.04), rgba(113, 63, 18, 0.04))',

        cartBackground: 'rgba(255, 253, 235, 0.95)',
        cartItemBackground: 'rgba(254, 249, 195, 0.85)',
        cartItemHover: 'rgba(254, 240, 138, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #FACC15 0%,
            #EAB308 100%
        )`,
      };

    case 'amarelo':
      return {
        // 🟠 TEMA AMARELO (âmbar/dourado) — tom do meio da família
        // amarelo/laranja.
        primaryGradient: `
          linear-gradient(135deg,
            #F59E0B 0%,
            #D97706 35%,
            #B45309 70%,
            #92400E 100%
        )`,
        primarySolid: '#D97706',
        primaryDark: '#92400E',

        surface: 'rgba(255, 251, 235, 0.95)',
        surfaceHover: 'rgba(254, 243, 199, 1)',
        glass: 'rgba(217, 119, 6, 0.3)',

        border: 'rgba(146, 64, 14, 0.22)',

        textPrimary: '#78350F',
        textSecondary: '#92400E',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(245, 158, 11, 0.88) 0%,
            rgba(217, 119, 6, 0.88) 35%,
            rgba(180, 83, 9, 0.88) 70%,
            rgba(146, 64, 14, 0.88) 100%
        )`,

        backgroundImage: `
          radial-gradient(ellipse at 50% 0%, rgba(217, 119, 6, 0.10) 0%, transparent 45%),
          radial-gradient(ellipse at 50% 100%, rgba(146, 64, 14, 0.08) 0%, transparent 45%),
          linear-gradient(135deg, #fffdf8 0%, #fef8ec 50%, #fdf1dc 100%)
        `,
        backgroundColor: '#FEF6E7',
        overlay: 'linear-gradient(rgba(120, 53, 15, 0.05), rgba(120, 53, 15, 0.05))',

        cartBackground: 'rgba(255, 251, 235, 0.95)',
        cartItemBackground: 'rgba(254, 243, 199, 0.85)',
        cartItemHover: 'rgba(253, 230, 138, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #D97706 0%,
            #92400E 100%
        )`,
      };

    case 'amareloescuro':
      return {
        // 🟠 TEMA AMARELO ESCURO — laranja queimado profundo, tom mais
        // fechado da família amarelo/laranja.
        primaryGradient: `
          linear-gradient(135deg,
            #FB923C 0%,
            #EA580C 35%,
            #C2410C 70%,
            #7C2D12 100%
        )`,
        primarySolid: '#EA580C',
        primaryDark: '#7C2D12',

        surface: 'rgba(255, 247, 237, 0.95)',
        surfaceHover: 'rgba(255, 237, 213, 1)',
        glass: 'rgba(234, 88, 12, 0.3)',

        border: 'rgba(124, 45, 18, 0.22)',

        textPrimary: '#7C2D12',
        textSecondary: '#9A3412',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(251, 146, 60, 0.88) 0%,
            rgba(234, 88, 12, 0.88) 35%,
            rgba(194, 65, 12, 0.88) 70%,
            rgba(124, 45, 18, 0.88) 100%
        )`,

        backgroundImage: `
          radial-gradient(ellipse at 50% 0%, rgba(234, 88, 12, 0.10) 0%, transparent 45%),
          radial-gradient(ellipse at 50% 100%, rgba(124, 45, 18, 0.08) 0%, transparent 45%),
          linear-gradient(135deg, #fffaf7 0%, #fef3ec 50%, #fde6d7 100%)
        `,
        backgroundColor: '#FFF3EA',
        overlay: 'linear-gradient(rgba(124, 45, 18, 0.05), rgba(124, 45, 18, 0.05))',

        cartBackground: 'rgba(255, 247, 237, 0.95)',
        cartItemBackground: 'rgba(255, 237, 213, 0.85)',
        cartItemHover: 'rgba(254, 215, 170, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #EA580C 0%,
            #7C2D12 100%
        )`,
      };

    case 'laranja':
      return {
        // 🟠 TEMA LARANJA — paleta pdvOrangeTheme (instrucao_layout_pdv_claude.md,
        // seção 16: primary/dark/background/surface/textos) + gradiente de
        // header definido na seção 2 do mesmo documento.
        primaryGradient: `linear-gradient(90deg, #080808 0%, #24120D 35%, #8F2F00 70%, #F57C00 100%)`,
        primarySolid: '#F57C00',
        primaryDark: '#E66F00',

        surface: '#FFFFFF',
        surfaceHover: '#FFF3E0',
        glass: 'rgba(245, 124, 0, 0.25)',

        border: '#E0E0E0',

        textPrimary: '#263238',
        textSecondary: '#607D8B',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `linear-gradient(90deg, rgba(8, 8, 8, 0.92) 0%, rgba(36, 18, 13, 0.92) 35%, rgba(143, 47, 0, 0.92) 70%, rgba(245, 124, 0, 0.92) 100%)`,

        backgroundImage: undefined,
        backgroundColor: '#FAFAFA',
        overlay: 'linear-gradient(rgba(48, 48, 48, 0.03), rgba(48, 48, 48, 0.03))',

        // Sem fundo próprio envolvendo a listagem inteira — só cada linha de
        // item tem fundo (cartItemBackground), o resto da área é transparente.
        cartBackground: 'transparent',
        cartItemBackground: '#FFFFFF',
        cartItemHover: 'rgba(245, 124, 0, 0.08)',
        inputBackground: `linear-gradient(90deg, #080808 0%, #F57C00 100%)`,

        barDark: '#303030',
        barAccent: '#F57C00',
        barDarkText: '#FFFFFF',
        barAccentText: '#FFFFFF',
      };

    case 'siac':
      return {
        // 🩵 TEMA SIAC — paleta oficial (marinho escuro + ciano de destaque)
        primaryGradient: `
          linear-gradient(135deg,
            #001E27 0%,
            #00505E 55%,
            #00E2F4 100%
          )
        `,
        primarySolid: '#00E2F4',
        primaryDark: '#001E27',

        surface: 'rgba(255, 255, 255, 0.97)',
        surfaceHover: 'rgba(224, 250, 253, 1)',
        glass: 'rgba(0, 226, 244, 0.22)',

        border: 'rgba(0, 30, 39, 0.15)',

        textPrimary: '#0B1B21',
        textSecondary: '#4B6570',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(0, 30, 39, 0.9) 0%,
            rgba(0, 80, 94, 0.9) 55%,
            rgba(0, 226, 244, 0.9) 100%
          )
        `,

        backgroundImage: undefined,
        backgroundColor: '#F0FBFD',
        overlay: 'linear-gradient(rgba(0, 30, 39, 0.05), rgba(0, 30, 39, 0.05))',

        cartBackground: 'rgba(255, 255, 255, 0.97)',
        cartItemBackground: 'rgba(224, 250, 253, 0.6)',
        cartItemHover: 'rgba(178, 244, 250, 0.7)',
        inputBackground: `
          linear-gradient(135deg,
            #001E27 0%,
            #00E2F4 100%
        )`,

        barDark: '#001E27',
        barAccent: '#00E2F4',
        barDarkText: '#FFFFFF',
        // Ciano claro precisa de texto escuro em cima pra manter contraste —
        // branco (usado nos outros temas) quase some sobre o #00E2F4.
        barAccentText: '#001E27',
      };

    case 'basico':
    default:
      return {
        // ⚫ TEMA BÁSICO ELEGANTE
        primaryGradient: `
          linear-gradient(135deg,
            #4B5563 0%,
            #374151 50%,
            #1F2937 100%
        )`,
        primarySolid: '#4B5563',
        primaryDark: '#374151',

        surface: 'rgba(249, 250, 251, 0.98)',
        surfaceHover: 'rgba(243, 244, 246, 1)',
        glass: 'rgba(75, 85, 99, 0.35)',

        border: 'rgba(209, 213, 219, 0.8)',

        textPrimary: '#111827',
        textSecondary: '#374151',
        textOnPrimary: '#FFFFFF',

        hoverGradient: `
          linear-gradient(135deg,
            rgba(75, 85, 99, 0.85) 0%,
            rgba(55, 65, 81, 0.85) 50%,
            rgba(31, 41, 55, 0.85) 100%
        )`,

        // Fundo claro/neutro do tema Básico — gradiente radial + linear.
        // As camadas extras (cortes diagonais nos cantos, grade de pontos,
        // formas rotacionadas com box-shadow) não cabem num background-image
        // só (dependem de mask-image e box-shadow independentes) — ver
        // PDVBasicoDecoration.tsx, renderizado por cima deste fundo em
        // Pdv.tsx quando o tema é 'basico'.
        backgroundImage: `
          radial-gradient(circle at 50% 45%, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 251, 0.96) 45%, rgba(232, 235, 239, 0.95) 100%),
          linear-gradient(135deg, #f8f9fb 0%, #eef1f4 55%, #e4e8ed 100%)
        `,
        backgroundColor: '#F9FAFB',
        overlay: 'linear-gradient(rgba(31, 41, 55, 0.05), rgba(31, 41, 55, 0.05))',

        cartBackground: 'rgba(249, 250, 251, 0.98)',
        cartItemBackground: 'rgba(243, 244, 246, 0.9)',
        cartItemHover: 'rgba(229, 231, 235, 1)',
        inputBackground: `
          linear-gradient(135deg,
            #4B5563 0%,
            #374151 100%
        )`,
      };
  }
};

// Hook para usar o tema — memoizado por `variant`: getThemeColors é pura,
// mas sem useMemo devolvia um objeto NOVO em toda renderização de quem
// chama (ex.: Pdv.tsx, que re-renderiza a cada tick do relógio), o que
// invalidava qualquer React.memo rio abaixo que recebesse `colors` como
// prop (ver CartItemsList/CartItemRow em Pdv.tsx).
export const usePDVTheme = (variant: ThemeVariant = 'siac') => {
  return useMemo(() => getThemeColors(variant), [variant]);
};
